import React from "react";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue
} from "react-hook-form";
import { mdiCheckDecagramOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";

import Button from "components/Button";
import Input from "components/Input";
import Select from "components/Select";
import {
  CloudDownloadSVGIcon,
  DataCheckSVGIcon,
  DollarCircleSvgIcon,
  LoadingSpinnerSVG,
  ManagePayIcon,
  ReceiptLongSVGIcon,
  SummarizeSvgIcon,
  TruckSVGIcon,
  WalletSvgIcon
} from "components/SVGIcons";
import Tooltip from "components/Tooltip";
import { formatNumber } from "helpers/formatNumber";
import { GridCardItem } from "models/Base";
import { IStepItem } from "models/Component";
import { IOption, ITabSwitch } from "models/Form";
import { IFactoringPaymentProfile, IPaymentSpeed, ITableHeaderList } from "models/HaulPay";
import { COLOR_BY_STATUS_FOR_MY_CARRIER } from "./base";
import { routes } from "./routes";
import PaymentSpeedInput from "../components/PaymentSpeedInput";
import { ICarriersFormData } from "../features/main/HaulPay/Broker/MyCarriers/Origin";
import styles from "../features/main/HaulPay/Broker/MyCarriers/Origin/style.module.scss";

export const BROKER_ITEMS: GridCardItem[] = [
  {
    id: 0,
    label: "Manage Pay",
    icon: (props: React.SVGProps<SVGSVGElement>) => <ManagePayIcon {...props} />,
    link: routes.HAULPAY_BROKER_MANAGE_PAY
  },
  {
    id: 1,
    label: "My Carriers",
    icon: (props: React.SVGProps<SVGSVGElement>) => <TruckSVGIcon {...props} />,
    link: routes.HAULPAY_BROKER_CARRIERS_ORIGIN
  },
  {
    id: 2,
    label: "My Wallet",
    icon: (props: React.SVGProps<SVGSVGElement>) => <WalletSvgIcon {...props} />,
    link: routes.HAULPAY_BROKER_WALLET_PENDING
  },
  {
    id: 3,
    label: "Reports",
    icon: (props: React.SVGProps<SVGSVGElement>) => <SummarizeSvgIcon {...props} />,
    link: routes.HAULPAY_BROKER_REPORTS
  },
  {
    id: 4,
    label: "Credit",
    icon: (props: React.SVGProps<SVGSVGElement>) => <DollarCircleSvgIcon {...props} />,
    link: routes.HAULPAY_CREDIT
  },
  {
    id: 5,
    label: "Receivables",
    icon: (props: React.SVGProps<SVGSVGElement>) => <ReceiptLongSVGIcon {...props} />,
    link: routes.HAULPAY_BROKER_RECEIVABLES
  }
];

export const BROKER_TAB_ITEMS: ITabSwitch[] = [
  {
    id: 0,
    label: "Pending Transactions",
    value: "pending-transactions",
    link: routes.HAULPAY_BROKER_WALLET_PENDING
  },
  {
    id: 1,
    label: "Statement History",
    value: "statement-history",
    link: routes.HAULPAY_BROKER_WALLET_HISTORY
  }
];

const IN_PROGRESS_REPORT_STATUSES = ["pending", "running"];
const UNAVAILABLE_REPORT_STATUSES = ["empty", "failed"];

export const isReportInProgress = (status?: string | null) =>
  IN_PROGRESS_REPORT_STATUSES.includes(String(status ?? "").toLowerCase());

export const isReportUnavailable = (status?: string | null) =>
  UNAVAILABLE_REPORT_STATUSES.includes(String(status ?? "").toLowerCase());

export const ReportAction = ({
  status,
  error,
  isLoading,
  onDownload,
  className
}: {
  status?: string | null;
  error?: string | null;
  isLoading: boolean;
  onDownload: VoidFunction;
  className?: string;
}) => {
  if (isReportInProgress(status)) {
    return (
      <span className={clsx("flex items-center gap-1 text-sm text-gray-500", className)}>
        <LoadingSpinnerSVG className="inline size-3 animate-spin text-text fill-primary" />
        Generating...
      </span>
    );
  }
  if (isReportUnavailable(status)) {
    const isEmpty = String(status).toLowerCase() === "empty";
    return (
      <span className={clsx("text-sm text-gray-500", className)} title={error ?? undefined}>
        {isEmpty ? "No data" : "Failed"}
        {!isEmpty && error ? <span className="block text-xs">{error}</span> : null}
      </span>
    );
  }
  return (
    <button type="button" className={className} onClick={onDownload}>
      {isLoading ? (
        <LoadingSpinnerSVG className="inline size-3 animate-spin text-text fill-primary mx-auto" />
      ) : (
        <CloudDownloadSVGIcon className="flex-none my-auto text-info" />
      )}
    </button>
  );
};

export const REPORTS_HEADER_LIST: ITableHeaderList[] = [
  {
    value: "action",
    label: "Actions",
    render: (item: {
      downloadReport: VoidFunction;
      isFetching: boolean;
      id: string;
      selectedId: string;
      status?: string | null;
      error?: string | null;
    }) => (
      <ReportAction
        className="!ml-4"
        status={item.status}
        error={item.error}
        isLoading={item.isFetching && item.selectedId === item.id}
        onDownload={item.downloadReport}
      />
    )
  },
  {
    value: "created",
    label: "Requested On",
    render: (item: string) => <span>{dayjs(item).format("MMM D, YYYY h:mm A")}</span>
  },
  {
    value: "reportType",
    label: "Type",
    render: (item: string) => <span className="capitalize">{item}</span>
  },
  {
    value: "details",
    label: "Date Range"
  }
];

export const WALLET_PENDING_HEADER_LIST: ITableHeaderList[] = [
  {
    value: "payments_due_date",
    label: "Date",
    dbSortName: "payments_due_date",
    render: (item: string) => {
      if (!item || !dayjs(item).isValid()) {
        return "-";
      }
      return dayjs(item).format("MM/DD/YYYY");
    }
  },
  {
    value: "description",
    label: "Description",
    dbSortName: "description_sort"
  },
  {
    value: "invoice_number",
    label: "Invoice",
    dbSortName: "fr_invoice_number"
  },
  {
    value: "credit_amount",
    label: "Credit",
    dbSortName: "credit_amount",
    render: (item: number) => formatNumber(item, 2)
  },
  {
    value: "debit_amount",
    label: "Debit",
    dbSortName: "debit_amount",
    render: (item: number) => formatNumber(item, 2)
  }
];

export const WALLET_HISTORY_HEADER_LIST: ITableHeaderList[] = [
  {
    value: "created",
    label: "Date",
    render: (item: string) => {
      return dayjs(item).format("MM/DD/YYYY");
    }
  },
  {
    value: "description",
    label: "Description"
  },
  {
    value: "invoice_number",
    label: "Invoice"
  },
  {
    value: "credit_amount",
    label: "Credit",
    render: (item: number) => formatNumber(item, 2)
  },
  {
    value: "debit_amount",
    label: "Debit",
    render: (item: number) => formatNumber(item, 2)
  },
  {
    value: "wallet_balance",
    label: "Balance",
    render: (item: number) => formatNumber(item, 2)
  }
];

export const CARRIER_FR_CONTRACT_TYPE = [
  {
    id: 0,
    label: "Factored",
    value: "STD_CARRIER"
  },
  {
    id: 1,
    label: "Non Factored",
    value: "NON_FACTORED_BILL_OUT"
  }
];

export const FR_CONTRACT_TYPE = [
  {
    id: 0,
    label: "Factored",
    value: "STD_BROKER"
  },
  {
    id: 1,
    label: "Non-Factored Pay Carrier",
    value: "NON_FACTORED_STANDARD"
  },
  {
    id: 2,
    label: "Non-Factored Bill Out Only",
    value: "NON_FACTORED_BILL_OUT"
  },
  {
    id: 3,
    label: "Non-Factored Payment Forward",
    value: "NON_FACTORED_PAYMENT_FORWARD",
    hidden: true
  },
  {
    id: 4,
    label: "Self-Financed No Invoicing",
    value: "SELF_FINANCE_NO_INVOICE"
  },
  {
    id: 5,
    label: "Self-Financed With Invoicing",
    value: "SELF_FINANCE_WITH_INVOICE"
  },
  {
    id: 8,
    label: "Self Finance w/ Invoice (Off-Platform)",
    value: "SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM"
  },
  {
    id: 6,
    label: "Self Finance Receivables",
    value: "SELF_FINANCE_RECEIVABLES",
    hidden: true
  },
  {
    id: 7,
    label: "Self Finance Ledger Deposit",
    value: "SELF_FINANCE_LEDGER_DEPOSIT",
    hidden: true
  }
];

export const ORIGIN_CARRIER_SEARCH_FILTER_LIST = [
  {
    id: 0,
    label: "Carrier Name",
    value: "carrier_dba"
  },
  {
    id: 1,
    label: "Status",
    value: "status"
  },
  {
    id: 2,
    label: "Email",
    value: "email"
  },
  {
    id: 3,
    label: "MC #",
    value: "mc"
  },
  {
    id: 4,
    label: "DOT #",
    value: "dot"
  },
  {
    id: 5,
    label: "Phone",
    value: "phone"
  }
];

export const ORIGIN_CARRIER_STATUS_FILTER_LIST = [
  {
    id: 0,
    label: "Pending",
    value: "pending"
  },
  {
    id: 1,
    label: "Needs Review",
    value: "needs_review"
  },
  {
    id: 2,
    label: "Approved",
    value: "approved"
  },
  {
    id: 3,
    label: "Declined",
    value: "declined"
  }
];

export const INVITED_CARRIER_SEARCH_FILTER_LIST = [
  {
    id: 0,
    label: "Email",
    value: "email"
  },
  {
    id: 1,
    label: "Phone Number",
    value: "phone_number"
  }
];

export const EQUIPMENT_TYPE_LIST = [
  {
    id: 0,
    label: "Other",
    value: "other"
  }
];

export const FIRST_PICK_UP_LIST = [
  {
    id: 0,
    label: "Anywhere",
    value: "anywhere"
  }
];

export const CREATE_WITH_MARGIN_ADVANCE = [
  {
    id: 0,
    label: "Yes",
    value: "yes"
  },
  {
    id: 1,
    label: "No",
    value: "no"
  }
];

export const PERCENTAGE_LIST = [
  {
    id: 0,
    label: "0%",
    value: "0",
    amount: 0
  },
  {
    id: 1,
    label: "50%",
    value: "50",
    amount: 50
  },
  {
    id: 2,
    label: "100%",
    value: "100",
    amount: 100
  }
];

export const UPLOAD_TYPE_LIST = [
  {
    id: 1,
    label: "HaulPay Upload",
    value: "haulpay_upload"
  }
];

export const VISIBILITY_LIST: IOption[] = [
  { id: 0, label: "Carrier Only", value: "carrier" },
  { id: 1, label: "Shipper/Debtor Only", value: "shipper,debtor" },
  { id: 2, label: "Carrier & Shipper/Debtor", value: "carrier,shipper,debtor" }
];

export const REPORTS_TABLE_HEADER_LIST: ITableHeaderList[] = [
  {
    value: "date",
    label: "Transaction Date"
  },
  {
    value: "invoice",
    label: "Invoice #"
  },
  {
    value: "debitAmount",
    label: "Debit Amount"
  },
  {
    value: "creditAmount",
    label: "Credit Amount"
  },
  {
    value: "description",
    label: "Description"
  }
];

export const BROKER_MY_CARRIER_TAB_ITEMS: ITabSwitch[] = [
  {
    id: 0,
    label: "My Carriers",
    value: "carriers",
    link: routes.HAULPAY_BROKER_CARRIERS_ORIGIN
  },
  {
    id: 1,
    label: "Invited Carriers",
    value: "invited-carriers",
    link: routes.HAULPAY_BROKER_INVITED_CARRIER
  }
];

export const PAYMENT_SPEED_LIST = [
  {
    id: 0,
    label: "1 Day",
    value: "1"
  },
  {
    id: 7,
    label: "7 Days",
    value: "7"
  },
  {
    id: 15,
    label: "15 Days",
    value: "15"
  },
  {
    id: 21,
    label: "21 Days",
    value: "21"
  },
  {
    id: 30,
    label: "30 Days",
    value: "30"
  }
];

export const PORTION_PERCENT_LIST = [
  {
    id: 0,
    label: "0%",
    value: "0"
  },
  {
    id: 50,
    label: "50%",
    value: "50"
  },
  {
    id: 100,
    label: "100%",
    value: "100"
  }
];

export const PAY_TAB_LIST = [
  {
    id: 0,
    label: "All",
    value: "all"
  },
  {
    id: 1,
    label: "In Process",
    value: "in_process"
  },
  {
    id: 2,
    label: "Pending",
    value: "pending"
  },
  {
    id: 3,
    label: "Incomplete",
    value: "incomplete"
  },
  {
    id: 4,
    label: "Pending",
    value: "special_pending"
  },
  {
    id: 5,
    label: "Incomplete",
    value: "pending_delivery"
  },
  {
    id: 6,
    label: "Document Review",
    value: "document_review"
  },
  {
    id: 7,
    label: "Non Factored",
    value: "non_factored"
  },
  {
    id: 8,
    label: "Paid",
    value: "paid"
  },
  {
    id: 9,
    label: "Document Issue",
    value: "document_issue"
  },
  {
    id: 10,
    label: "Fuel Advance",
    value: "fuel_advance"
  },
  {
    id: 11,
    label: "Approved",
    value: "approved"
  },
  {
    id: 12,
    label: "Declined",
    value: "declined"
  },
  {
    id: 13,
    label: "Carrier Paid",
    value: "carrier_paid"
  },
  {
    id: 14,
    label: "Invoice Paid",
    value: "invoice_paid"
  },
  {
    id: 15,
    label: "Pending Originals",
    value: "pending_originals"
  }
];

export const PAY_STATUS_LIST = [
  {
    id: 0,
    label: "Incomplete",
    value: "Pending Delivery"
  },
  {
    id: 1,
    label: "Pending",
    value: "Special Pending"
  },
  {
    id: 2,
    label: "Declined",
    value: "Declined"
  },
  {
    id: 3,
    label: "Document Issue",
    value: "Document Issue"
  },
  {
    id: 4,
    label: "Approved",
    value: "Remote Approved"
  }
];

export const FR_STATUS = [
  {
    id: 0,
    value: "Pending Delivery",
    label: "Incomplete"
  },
  {
    id: 2,
    value: "Special Pending",
    label: "Pending"
  },
  {
    id: 1,
    value: "Hold",
    label: "Hold"
  },
  {
    id: 3,
    value: "Declined",
    label: "Declined"
  },
  {
    id: 4,
    value: "Document Issue",
    label: "Document Issue"
  },
  {
    id: 5,
    value: "Remote Approved",
    label: "Approved"
  },
  {
    id: 6,
    value: "NON_FACTORED_BILL_OUT",
    label: "Non-Factored Bill Out Only"
  },
  {
    id: 7,
    value: "NON_FACTORED_STANDARD",
    label: "Non-Factored Pay carrier"
  },
  {
    id: 8,
    value: "paid",
    label: "Paid"
  },
  {
    id: 9,
    value: "Haulpay Review",
    label: "Haulpay Review"
  }
];

export const ASSIGNED_LOADS_TAB_DATA = [
  {
    label: "All",
    value: "all"
  },
  {
    label: "Incomplete",
    value: "incomplete"
  },
  {
    label: "Pending",
    value: "pending"
  }
];

export const MY_CARRIERS_TABLE_HEADER_LIST = (
  carriers: IFactoringPaymentProfile[],
  onSaveCarrier: (index: number, id: string) => void,
  handleToggleInfoModal: (id: string) => void,
  defaultPaymentSettings: IPaymentSpeed[],
  register: UseFormRegister<Partial<ICarriersFormData>>,
  control: Control<Partial<ICarriersFormData>>,
  getValues: UseFormGetValues<Partial<ICarriersFormData>>,
  setValue: UseFormSetValue<Partial<ICarriersFormData>>,
  savingCarrierId?: string | null
): ITableHeaderList[] => [
  {
    value: "status",
    label: "Status",
    render: (item: {
      carrierStatus: string;
      statusDate: string;
      pendingApprovalTasks: string[];
    }) => (
      <div className={styles.tooltipGroup}>
        <Tooltip
          position="right"
          message={
            item.pendingApprovalTasks.length === 0 ? (
              <span className="text-sm">No Pending Approval Tasks</span>
            ) : (
              <div className="text-sm text-center">
                <p>Pending Approval Tasks</p>
                <ul className="mt-1.5">
                  {item.pendingApprovalTasks.map((task) => (
                    <li key={task}>- {task}</li>
                  ))}
                </ul>
              </div>
            )
          }>
          <div
            className={clsx(
              "rounded-3xl justify-center px-3 py-1.5 text-xs flex items-center gap-1.5 !bg-transparent",
              COLOR_BY_STATUS_FOR_MY_CARRIER[item.carrierStatus]
            )}>
            {item.carrierStatus === "approved" ? (
              <Icon path={mdiCheckDecagramOutline} className="flex-none size-5" />
            ) : (
              <DataCheckSVGIcon className="flex-none size-5" />
            )}
            {item.carrierStatus.split("_").map((word, index) => (
              <span key={index} className="capitalize">
                {word}{" "}
              </span>
            ))}
          </div>
        </Tooltip>
        <div
          className={clsx(
            styles.tooltipDate,
            "text-sm mt-1 !bg-transparent !border-none tooltip-date"
          )}>
          {dayjs(item.statusDate).format("M/D/YYYY HH:mm A")}
        </div>
      </div>
    )
  },
  {
    value: "payment_profile.owner_email",
    label: "Email",
    rowStyle: "!w-52 !break-all"
  },
  {
    value: "payment_profile.company_name",
    label: "Carrier Name",
    rowStyle: "!w-32",
    render: (item: string, index: number) => (
      <div className="flex flex-col items-start gap-2">
        <button
          type="button"
          className="text-left text-info"
          onClick={() => handleToggleInfoModal(carriers[index].id)}>
          {item}
        </button>
        {carriers[index]?.payment_profile?.factoring_company?.name ? (
          <span className="text-pendingBorder">
            {carriers[index]?.payment_profile.factoring_company?.name}
          </span>
        ) : null}
      </div>
    )
  },
  {
    value: "payment_profile.owner_phone_number_cell",
    label: "Phone"
  },
  {
    value: "payment_profile.company_mc",
    label: "MC#"
  },
  {
    value: "payment_profile.company_dot",
    label: "DOT#"
  },
  {
    value: "payment_speed",
    label: "Payment Speed",
    render: (_paymentSpeedTerms: any, index: number) => {
      return (
        <PaymentSpeedInput
          noLabel
          skip
          defaultPaymentSpeed={defaultPaymentSettings}
          control={control}
          name={`payment_speed.${index}`}
          customOnChange={(paymentSpeed) => {
            const rate = paymentSpeed.discountRate ?? paymentSpeed.rate ?? 0;
            setValue(
              `carrier_rate.${index}`,
              Number(rate * (100 - Number(getValues(`broker_portion.${index}.value`)))) / 100 + "%"
            );
          }}
          {...(index === 0 ? { direction: "down" } : { direction: "up" })}
        />
      );
    }
  },
  {
    value: "broker_portion",
    label: "Broker Portion",
    render: (_: any, index: number) => (
      <Controller
        name={`broker_portion.${index}`}
        control={control}
        render={({ field: { value, onChange } }: any) => (
          <Select
            value={value}
            disabled={carriers[index]?.factoring_company_relation !== null}
            options={PORTION_PERCENT_LIST}
            onChange={(brokerPortion) => {
              const rate =
                getValues(`payment_speed.${index}`)?.rate ??
                getValues(`payment_speed.${index}`)?.amount;
              rate &&
                setValue(
                  `carrier_rate.${index}`,
                  Number(rate * (100 - Number(brokerPortion.value))) / 100 + "%"
                );
              onChange(brokerPortion);
            }}
            {...(index === 0 ? { direction: "down" } : { direction: "up" })}
          />
        )}
      />
    )
  },
  {
    value: "carrier_rate",
    label: "Carrier Rate",
    render: (value: any, index: number) => (
      <Input
        disabled
        name={`carrier_rate.${index}`}
        register={register}
        inputClass="!border-icon !rounded-lg !p-1.5 desktop:!p-2.5 h-7 desktop:h-10 text-xs desktop:text-sm"
      />
    )
  },
  {
    value: "external_code",
    label: "External Code",
    render: (_: any, index: number) => (
      <Input
        type="text"
        name={`external_code.${index}`}
        register={register}
        inputClass="!border-icon !rounded-lg !p-1.5 desktop:!p-2.5 h-7 desktop:h-10 text-xs desktop:text-sm"
      />
    )
  },
  {
    value: "action",
    label: "Action",
    render: (_: any, index: number, id: string) => (
      <Button
        className="bg-background"
        onClick={() => onSaveCarrier(index, id)}
        loading={savingCarrierId === id}>
        Save
      </Button>
    )
  }
];

export const CONNECT_EXISTING_PROFILE_STEP_LIST: IStepItem[] = [
  { id: 1, name: "Add a Carrier", description: "In four easy steps, add a new carrier" },
  { id: 2, name: "Add a Carrier", description: "In four easy steps, add a new carrier" },
  { id: 3, name: "Upload Documents", description: "In four easy steps, add a new carrier" },
  { id: 4, name: "Connection Successful", description: "In four easy steps, add a new carrier" }
];

export const INVITE_CARRIER_STEP_LIST: IStepItem[] = [
  { id: 2, name: "Add a Carrier", description: "In three easy steps, add a new carrier" },
  { id: 3, name: "Upload Documents", description: "In three easy steps, add a new carrier" },
  { id: 4, name: "Connection Successful", description: "In three easy steps, add a new carrier" }
];

export const INVITED_CARRIER_PROFILE_STEP_LIST: IStepItem[] = [
  {
    id: 1,
    name: "Create Carrier Payment Profile",
    description: "In four easy steps, add a new carrier"
  },
  { id: 2, name: "Add a Carrier", description: "In four easy steps, add a new carrier" },
  { id: 3, name: "Upload Documents", description: "In four easy steps, add a new carrier" },
  { id: 4, name: "Payment Profile Create", description: "In four easy steps, add a new carrier" }
];

export const REPORT_TYPE_LIST = [
  {
    id: 0,
    label: "Funding Report",
    value: "funding_report"
  },
  {
    id: 1,
    label: "AR Report",
    value: "ar_report"
  },
  {
    id: 4,
    label: "Open AR Report",
    value: "open_ar_report"
  },
  {
    id: 5,
    label: "Debtor Aging",
    value: "debtor_aging_by_bucket"
  },
  {
    id: 6,
    label: "Invoice Settlement",
    value: "settlement_report"
  },
  {
    id: 7,
    label: "Chargeback / Giveback Report",
    value: "chargeback_giveback_report"
  },
  {
    id: 8,
    label: "Self-Finance Funding Report",
    value: "self_finance_report"
  },
  {
    id: 9,
    label: "Wallet Transaction Report",
    value: "wallet_transaction"
  },
  {
    id: 10,
    label: "Off-Platform Funding Report",
    value: "off_platform_self_finance_report"
  }
];

export const FILTER_BY_LIST = [
  {
    id: 0,
    label: "Past 30 days",
    value: "past-30-days"
  },
  {
    id: 1,
    label: "Past 60 days",
    value: "past-60-days"
  },
  {
    id: 2,
    label: "Past 90 days",
    value: "past-90-days"
  },
  {
    id: 3,
    label: "Year to Date",
    value: "ytd"
  },
  {
    id: 4,
    label: "Past 6 months",
    value: "past-6-months"
  },
  {
    id: 5,
    label: "Past 1 year",
    value: "past-1-year"
  }
];

export const EXTRA_DATA: IOption[] = [
  {
    id: 0,
    label: "--",
    value: "none"
  }
];

export const CUSTOMER_TRANS_ID_TYPES: IOption[] = [
  { id: 1, label: "MC", value: "mc" },
  { id: 2, label: "DOT", value: "dot" },
  { id: 3, label: "TAX ID", value: "tax_id" },
  { id: 4, label: "DUNS", value: "duns" }
];

export const FR_LINE_TYPE_LIST: IOption[] = [
  { id: 0, label: "Linehaul", value: "Linehaul" },
  { id: 1, label: "Container Yard Storage", value: "Container Yard Storage" },
  { id: 2, label: "Late Fee", value: "Late Fee" },
  { id: 3, label: "Detention Charge", value: "Detention Charge" },
  { id: 4, label: "Lumper (Paid by Carrier)", value: "Lumper (Paid by Carrier)" },
  { id: 5, label: "Driver Assist", value: "Driver Assist" },
  { id: 6, label: "Layover", value: "Layover" },
  { id: 7, label: "Fuel", value: "Fuel" },
  { id: 8, label: "Lumper Fee (Paid by Client)", value: "Lumper Fee (Paid by Client)" },
  { id: 9, label: "Fuel Surcharge", value: "Fuel Surcharge" },
  { id: 10, label: "Client Other Charges", value: "Client Other Charges" },
  { id: 11, label: "Trailer Storage", value: "Trailer Storage" },
  { id: 12, label: "Other", value: "Other" }
];
export const DUPLICATED_INVOICE_TABLE_HEADER_LIST: ITableHeaderList[] = [
  {
    value: "invoice_number",
    label: "Invoice #"
  },
  {
    value: "paymentProfile.company_name",
    label: "Carrier"
  },
  {
    value: "debtor.dba",
    label: "Debtor"
  },
  {
    value: "amount",
    label: "Invoice Amt.",
    render: (item: number) => <span>{item ? formatNumber(item / 100, 2) : "-"}</span>
  },
  {
    value: "linkToInvoice",
    label: "Link to Invoice",
    render: (item: { action: VoidFunction }) => (
      <Button type="button" onClick={item.action}>
        More
      </Button>
    )
  }
];
